// src/Views/WorkWithMePage.js — Flasher WorkWithMeSection.
// Two-paths cards + pricing table + Calendly popup CTA.
import React, { useEffect } from 'react';
import { Eyebrow, MagneticButton } from '../components/primitives';
import { ENGAGEMENT_GROUPS } from './siteData';

const CALENDLY_URL = 'https://calendly.com/solomon-foshko-designbydialogue/30min';

const PATHS = [
  {
    title: 'I Build It',
    accent: 'var(--magenta)',
    body:
      'You have a problem, a domain, an idea that needs to become a working system. ' +
      'I bring the method: systems thinking, build discipline, AI agent coordination. ' +
      'We go from concept to working product, zero to one.',
  },
  {
    title: 'I Teach You to Build',
    accent: 'var(--cyan)',
    body:
      "Most people trying to build with AI have the tools. What they're missing is a way to " +
      "think that matches what the tools can now do. The bottleneck isn't capability; " +
      "it's clarity. I teach that.",
  },
];

const WorkWithMePage = () => {
  useEffect(() => {
    // Belt-and-suspenders: Calendly is also loaded from the NavBar effect, but
    // load it here too so this section works when mounted alone.
    const existingLink = document.querySelector('link[href*="calendly"]');
    if (!existingLink) {
      const link = document.createElement('link');
      link.href = 'https://assets.calendly.com/assets/external/widget.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    const existingScript = document.querySelector('script[src*="calendly"]');
    if (!existingScript) {
      const script = document.createElement('script');
      script.src = 'https://assets.calendly.com/assets/external/widget.js';
      script.type = 'text/javascript';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  const handleSchedule = (e) => {
    e.preventDefault();
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section id="work" style={{ position: 'relative', padding: '120px 28px', zIndex: 2 }}>
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 32,
            marginBottom: 56,
            flexWrap: 'wrap',
          }}
        >
          <div>
            <Eyebrow accent="var(--magenta)">⏵ WORK WITH ME — Two paths</Eyebrow>
            <h2
              style={{
                fontFamily: 'var(--f-display)',
                fontSize: 'clamp(48px, 7vw, 112px)',
                fontWeight: 700,
                lineHeight: 0.94,
                letterSpacing: '-0.04em',
                margin: 0,
                color: 'var(--ink)',
              }}
            >
              I build,{' '}
              <span
                style={{
                  fontStyle: 'italic',
                  fontWeight: 400,
                  background: 'linear-gradient(120deg, var(--magenta), var(--purple), var(--cyan))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  paddingRight: '0.18em',
                }}
              >
                or I teach
              </span>{' '}
              you how.
            </h2>
          </div>
          <p style={{ maxWidth: 420, fontSize: 16, lineHeight: 1.55, color: 'var(--ink-2)', margin: 0 }}>
            The domain is yours. The build capability is mine. We start with your problem,
            define what success looks like, and build with you in the decision seat.
          </p>
        </div>

        <div
          className="paths-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: 20,
            marginBottom: 48,
          }}
        >
          {PATHS.map((p) => (
            <div
              key={p.title}
              style={{
                position: 'relative',
                padding: 32,
                borderRadius: 20,
                background: 'rgba(15, 13, 42, 0.55)',
                border: '1px solid var(--line)',
                backdropFilter: 'blur(8px)',
                WebkitBackdropFilter: 'blur(8px)',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 2,
                  background: p.accent,
                  boxShadow: `0 0 14px ${p.accent}`,
                }}
              />
              <h3
                style={{
                  fontFamily: 'var(--f-display)',
                  fontSize: 32,
                  fontWeight: 600,
                  margin: '0 0 14px',
                  letterSpacing: '-0.02em',
                  color: 'var(--ink)',
                }}
              >
                {p.title}
              </h3>
              <p style={{ fontSize: 15, lineHeight: 1.6, color: 'var(--ink-2)', margin: 0 }}>
                {p.body}
              </p>
            </div>
          ))}
        </div>

        {/* Pricing card */}
        <div
          style={{
            padding: 36,
            borderRadius: 24,
            background: 'rgba(15, 13, 42, 0.6)',
            border: '1px solid var(--line-strong)',
            backdropFilter: 'blur(10px)',
            WebkitBackdropFilter: 'blur(10px)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              marginBottom: 28,
              paddingBottom: 18,
              borderBottom: '1px solid var(--line)',
              flexWrap: 'wrap',
            }}
          >
            <h3
              style={{
                fontFamily: 'var(--f-display)',
                fontSize: 32,
                fontWeight: 600,
                margin: 0,
                letterSpacing: '-0.02em',
                color: 'var(--ink)',
                flex: 1,
              }}
            >
              Engagement Models
            </h3>
            <span
              style={{
                fontFamily: 'var(--f-mono)',
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--ink-3)',
              }}
            >
              MSA + per-engagement SOW
            </span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {ENGAGEMENT_GROUPS.map((g) => (
              <div key={g.label}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 14,
                    marginBottom: 14,
                    flexWrap: 'wrap',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'var(--f-mono)',
                      fontSize: 11,
                      letterSpacing: '0.18em',
                      textTransform: 'uppercase',
                      padding: '4px 10px',
                      borderRadius: 999,
                      background: `${g.accent}18`,
                      color: g.accent,
                      border: `1px solid ${g.accent}55`,
                    }}
                  >
                    {g.label}
                  </span>
                  <span style={{ fontFamily: 'var(--f-body)', fontSize: 14, color: 'var(--ink-3)' }}>
                    {g.sublabel}
                  </span>
                </div>
                {g.items.map((it, i, arr) => (
                  <div
                    key={it.name}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'minmax(0, 1fr) auto',
                      gap: 24,
                      padding: '14px 0',
                      borderBottom: i === arr.length - 1 ? 'none' : '1px dashed var(--line)',
                      alignItems: 'baseline',
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>
                        {it.name}
                      </div>
                      <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--ink-2)' }}>
                        {it.note}
                      </div>
                    </div>
                    <div
                      style={{
                        fontFamily: 'var(--f-mono)',
                        fontSize: 14,
                        color: g.accent,
                        whiteSpace: 'nowrap',
                        textShadow: `0 0 16px ${g.accent}55`,
                      }}
                    >
                      {it.price}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: 28,
              paddingTop: 20,
              borderTop: '1px solid var(--line)',
              fontFamily: 'var(--f-mono)',
              fontSize: 12,
              lineHeight: 1.6,
              color: 'var(--ink-3)',
              letterSpacing: '0.04em',
            }}
          >
            <span style={{ color: 'var(--magenta)' }}>{'>'}</span>{' '}
            Milestones with defined deliverables, dependencies, and acceptance criteria.
            Deposit on signing; net 15–30 on milestone acceptance.
          </div>
        </div>

        {/* Calendly CTA */}
        <div
          style={{
            marginTop: 32,
            padding: '32px 28px',
            borderRadius: 16,
            border: '1px solid var(--line)',
            background: 'rgba(15, 13, 42, 0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 16,
            textAlign: 'center',
          }}
        >
          <p style={{ fontSize: 16, color: 'var(--ink-2)', margin: 0, maxWidth: 520 }}>
            If something here resonated — whether you want to build together or learn the method —
            grab a slot and let's talk.
          </p>
          <MagneticButton size="lg" accent="magenta" onClick={handleSchedule}>
            Schedule time to talk <span>↗</span>
          </MagneticButton>
        </div>
      </div>
    </section>
  );
};

export default WorkWithMePage;
